import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DataProvider from '@app/model/_common/DataProvider';
import CompetitorReviewsState from '@app/model/review/state/CompetitorReviewsState';


const ReviewsPlatformDistributionGraph: React.FunctionComponent<{
	reviewsState: CompetitorReviewsState
}> = ({ reviewsState }) => {

	const options: Highcharts.Options = useMemo(() => {
		// Provider name mapping
		const getProviderName = (provider: DataProvider): string => {
			const nameMap: Record<DataProvider, string> = {
				[DataProvider.BOOKING]: 'Booking.com',
				[DataProvider.GOOGLE]: 'Google',
				[DataProvider.TRIP_ADVISOR]: 'TripAdvisor',
				[DataProvider.HOTELS_COM]: 'Hotels.com',
				[DataProvider.AGODA]: 'Agoda',
				[DataProvider.EXPEDIA]: 'Expedia',
				[DataProvider.TRIP_COM]: 'Trip.com',
				[DataProvider.PRICELINE]: 'Priceline',
				[DataProvider.WEBHOTELIER]: 'WebHotelier',
				[DataProvider.HILTON]: 'Hilton',
				[DataProvider.BRAND_COM]: 'Brand.com',
				[DataProvider.HOLIDAYCHECK]: 'HolidayCheck',
				[DataProvider.TRAVELSUPERMARKET]: 'TravelSupermarket',
				[DataProvider.TUIUK]: 'TUI UK',
				[DataProvider.TUIDE]: 'TUI DE',
				[DataProvider.JET2]: 'Jet2',
				[DataProvider.AIRBNB]: 'Airbnb',
			};
			return nameMap[provider] ?? provider;
		};

		// Provider color mapping
		const getProviderColor = (provider: DataProvider): string => {
			const colorMap: Record<DataProvider, string> = {
				[DataProvider.GOOGLE]: '#4285F4',
				[DataProvider.BOOKING]: '#a855f7',
				[DataProvider.TRIP_ADVISOR]: '#5cb85c',
				[DataProvider.HOTELS_COM]: '#d9534f',
				[DataProvider.AGODA]: '#f0ad4e',
				[DataProvider.EXPEDIA]: '#0275d8',
				[DataProvider.TRIP_COM]: '#5bc0de',
				[DataProvider.PRICELINE]: '#292b2c',
				[DataProvider.WEBHOTELIER]: '#636c72',
				[DataProvider.HILTON]: '#0275d8',
				[DataProvider.BRAND_COM]: '#5cb85c',
				[DataProvider.HOLIDAYCHECK]: '#f0ad4e',
				[DataProvider.TRAVELSUPERMARKET]: '#d9534f',
				[DataProvider.TUIUK]: '#2563eb',
				[DataProvider.TUIDE]: '#0ea5e9',
				[DataProvider.JET2]: '#ef4444',
				[DataProvider.AIRBNB]: '#FF5A5F',
			};
			return colorMap[provider] ?? '#7cb5ec';
		};

		// Extract data from provider states
		const pieData = reviewsState.stateStatisticsForProviders.map(state => ({
			name: getProviderName(state.provider),
			y: state.statistics.numberOfReviews,
			color: getProviderColor(state.provider),
		}));

		return {
			chart: {
				type: 'pie',
			},
			title: {
				text: undefined,
			},
			tooltip: {
				pointFormat: '<b>{point.y}</b> reviews ({point.percentage:.1f}%)',
			},
			plotOptions: {
				pie: {
					allowPointSelect: true,
					cursor: 'pointer',
					dataLabels: {
						enabled: true,
						format: '<b>{point.name}</b>: {point.percentage:.1f}%',
						distance: 20,
					},
					showInLegend: true,
				},
			},
			legend: {
				align: 'center',
				verticalAlign: 'bottom',
				layout: 'horizontal',
			},
			series: [
				{
					name: 'Reviews',
					type: 'pie',
					data: pieData,
				},
			],
			credits: {
				enabled: false,
			},
		};
	}, [reviewsState.stateStatisticsForProviders]);

	return <div className="h-full w-full flex items-center justify-center p-4">
		<div className="w-full" style={{ height: '100%' }}>
			<HighchartsReact
				highcharts={Highcharts}
				options={options}
				containerProps={{ style: { height: '100%', width: '100%' } }}
			/>
		</div>
	</div>;

};

export default ReviewsPlatformDistributionGraph;
